import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useUserStore } from '../../store/userStore';
import { startRefreshTimer } from '../../api/api';
import { getSessionStore } from '../../store/sessionStore';
import { resolvePostLoginRoute, resolvePromptCount } from '../../utils/userProfile';
import { PageBusyLoader } from '../ui/PageBusyLoader';

/**
 * LoginSuccess Component
 * Handles OAuth redirect from backend after successful Google authentication
 * Backend redirects here with token in query params: /login/success?token=JWT
 */
const LoginSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setUser = useUserStore((state) => state.setUser);
  const setTokens = useUserStore((state) => state.setTokens);

  useEffect(() => {
    let toastShown = false;

    const handleSuccess = async () => {
      try {
        const token = searchParams.get('token');
        const refresh = searchParams.get('refreshToken');

        if (!token) {
          throw new Error('No authentication token received');
        }

        localStorage.setItem('accessToken', token);
        if (refresh) {
          localStorage.setItem('refreshToken', refresh);
        }

        setTokens({ accessToken: token, refreshToken: refresh });

        // Start refresh timer
        startRefreshTimer();

        const session = await getSessionStore().bootstrapSession({
          force: true,
        });
        const userData = session.user;
        if (userData) {
          setUser(userData as never);
        }
        const promptCount = resolvePromptCount(session.prompts ?? []);
        const userRecord = userData as Record<string, unknown> | null;

        if (!toastShown) {
          toast.success('Login successful!');
          toastShown = true;
        }

        navigate(resolvePostLoginRoute(userRecord, promptCount));
      } catch (err: any) {
        console.error('Login success handling error:', err);
        if (!toastShown) {
          toast.error(err.message || 'Failed to process authentication');
          toastShown = true;
        }
        navigate('/');
      }
    };

    handleSuccess();
  }, [searchParams, navigate, setUser, setTokens]);

  return <PageBusyLoader pageName="login" label="Completing login..." overlay />;
};

export default LoginSuccess;
